import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { AccountBalance, AccountDto, BalanceBackofficeDto, BalanceControllerService } from '@api/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { NotificationService } from '@shared/services/notifications/notifications.service';
import { asOptional } from '@stillmandigital/common';
import { pipe, switchMap } from 'rxjs';

export interface AccountBalanceViewModel extends AccountBalance {
  balances: BalanceBackofficeViewModel[];
}

export interface BalanceBackofficeViewModel extends BalanceBackofficeDto {
  id: string;
}

export const DOWNLOAD_BALANCES_KEY = 'download-balances';

export const useBalances = () =>
  signalStoreFeature(
    {
      props: {
        _notificationService: type<NotificationService>(),
      },
      state: {
        accounts: type<AccountDto[]>(),
      },
    },
    withState({
      accountBalance: asOptional<AccountBalanceViewModel>(),
    }),
    withProps(() => ({
      _balanceService: inject(BalanceControllerService),
    })),
    withComputed(store => {
      const balances = computed(() => store.accountBalance()?.balances || []);

      return {
        balances,
      };
    }),
    withMethods(store => {
      const loadAccountBalance = rxMethod<number>(
        pipe(
          switchMap(accountId =>
            store._balanceService.getBalancesV2({ accountId }).pipe(
              tapResponse({
                next: accountBalance =>
                  patchState(store, {
                    accountBalance: {
                      ...accountBalance,
                      balances: accountBalance.balances?.map(toBalanceBackofficeViewModel) || [],
                    },
                  }),
                error: (error: HttpErrorResponse) =>
                  store._notificationService.showErrorNotification(
                    `Failed to load balances for accountId ${accountId}`,
                    error,
                  ),
              }),
            ),
          ),
        ),
      );
      const getAccountWithBalanceByIds = (accountId: number, assetId: number) => {
        const account = store.accounts().find(acc => acc.id === accountId);
        const balance = store
          .accountBalance()
          ?.balances.find(balance => balance.accountId === accountId && balance.assetId === assetId);

        return { account, balance };
      };

      return {
        loadAccountBalance,
        getAccountWithBalanceByIds,
      };
    }),
  );

export function toBalanceBackofficeViewModel(balance: BalanceBackofficeDto): BalanceBackofficeViewModel {
  return {
    ...balance,
    id: `${balance.assetId}-${balance.accountId}`,
  };
}
